/**
 * RegimeBackdrop — compact live-regime banner for pages that benefit from
 * regime context (Risk Radar, Trade Ideas, Catalysts, Hidden Alpha, etc.).
 *
 * Reads /api/macro/regime/compact, renders a one-line coloured strip with
 * score + label + 24h direction + a verdict-copy line relevant to the
 * host page. Refreshes every 60 seconds.
 */
import { useEffect, useState } from 'react'
import { api } from '@/lib/api'

type Data = {
  score: number
  label: string
  band: string
  delta_24h: number | null
  direction: 'improving' | 'deteriorating' | 'flat'
}

// Module-level cache so navigating between pages doesn't refetch within
// the TTL window. 30s is aggressive enough to feel live, long enough to
// skip the redundant call when the user opens 3 pages in quick succession.
const CACHE_TTL_MS = 30_000
let _cache: { data: Data; at: number } | null = null

type Variant = 'risk' | 'ideas' | 'catalysts' | 'generic'

const VERDICTS: Record<Variant, (score: number) => string> = {
  risk: (s) =>
    s >= 2.5 ? 'Breakout regime — risks less acute, stay alert to reversals.'
    : s >= 1 ? 'Risk-on — bearish risks can be judged more patiently.'
    : s <= -2.5 ? 'PANIC — treat every high-priority risk as action-today.'
    : s <= -1 ? 'Risk-off — tighten tolerances; medium risks deserve review.'
    : 'Neutral — standard risk thresholds apply.',
  ideas: (s) =>
    s >= 2.5 ? 'Breakout — lean into momentum setups with boosted sizing.'
    : s >= 1 ? 'Risk-on — LONG ideas carry regime tailwind.'
    : s <= -2.5 ? 'PANIC — only highest-conviction setups; sizing at 0.5×.'
    : s <= -1 ? 'Risk-off — favour SHORT setups; trim LONG ideas.'
    : 'Neutral — pair & factor trades preferred over beta.',
  catalysts: (s) =>
    s >= 1 ? 'Risk-on — positive catalysts get more reaction.'
    : s <= -1 ? 'Risk-off — negative catalysts dominate; positive catalysts fade.'
    : 'Neutral — catalyst reactions closer to fundamentals.',
  generic: (s) =>
    s >= 1 ? 'Risk-on backdrop.'
    : s <= -1 ? 'Risk-off backdrop.'
    : 'Neutral regime.',
}

export default function RegimeBackdrop({ variant = 'generic' }: { variant?: Variant }) {
  const [data, setData] = useState<Data | null>(() => {
    // Hydrate from cache if still warm
    if (_cache && Date.now() - _cache.at < CACHE_TTL_MS) return _cache.data
    return null
  })

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      // Skip if cache is still fresh
      if (_cache && Date.now() - _cache.at < CACHE_TTL_MS) {
        if (!cancelled) setData(_cache.data)
        return
      }
      try {
        const res = await api.get('/api/macro/regime/compact')
        if (!cancelled && res.data?.score !== undefined) {
          _cache = { data: res.data, at: Date.now() }
          setData(res.data)
        }
      } catch {
        /* silent */
      }
    }
    load()
    const iv = setInterval(load, 60_000)
    return () => {
      cancelled = true
      clearInterval(iv)
    }
  }, [])

  if (!data) return null

  // Higher-contrast tints — /10 + text-X-300 reads as dim brown on dark
  // theme. Bumping opacity + brighter text so the regime context is
  // actually visible at a glance.
  const tone =
    data.score >= 1 ? 'bg-emerald-500/20 border-emerald-500/50 text-emerald-200'
    : data.score <= -1 ? 'bg-rose-500/20 border-rose-500/50 text-rose-200'
    : 'bg-zinc-500/20 border-zinc-500/50 text-zinc-100'

  const verdict = VERDICTS[variant](data.score)

  return (
    <div className={`rounded-lg border px-4 py-2.5 ${tone}`}>
      <div className="flex items-center gap-3 flex-wrap text-sm">
        <span className="text-[10px] uppercase tracking-wider font-bold opacity-70">
          Regime
        </span>
        <span className="font-mono font-bold">
          {data.score >= 0 ? '+' : ''}{data.score.toFixed(2)}
        </span>
        <span className="font-semibold">{data.label}</span>
        {data.delta_24h !== null && (
          <span className="text-[11px] opacity-80">
            {data.direction === 'improving'
              ? '↗'
              : data.direction === 'deteriorating'
                ? '↘'
                : '→'}
            {' '}
            24h {data.delta_24h >= 0 ? '+' : ''}
            {data.delta_24h.toFixed(2)}
          </span>
        )}
        <span className="text-xs opacity-80 ml-auto">{verdict}</span>
      </div>
    </div>
  )
}
